import { MediaRef } from "@/app/__to_be_integrated/_____components/image/enhanced-image/types/types";
import ScrollDownArrows from "@/app/features/shared/__to_be_integrated/ui/ScrollDownArrows";
import clsx from "clsx";
import Image from "next/image";
import { ReactNode } from "react";
import styles from "./SlideshowIntroV2.module.css";
import { Typography } from "@mui/material";
import { mediaRefAlt, mediaRefUrl } from "@/app/features/shared/__to_be_integrated/types/general/media/media-helper";
import { PLACEHOLDER_IMAGE } from "@/app/types/common/constants";

interface SlideshowIntroV2Props {
    title: string;
    subtitle?: string | null;
    images: MediaRef[];
    startChildren?: ReactNode;
    heightPreset?: "full" | "reduced";
    slideDurationMs?: number;
    fadeDurationMs?: number;
    content?: ReactNode;
    linkContentToAnchor?: boolean;
}

const DEFAULT_IMAGE = PLACEHOLDER_IMAGE;
const INTRO_ANCHOR_ID = "intro-anchor";
const DEFAULT_SLIDE_DURATION_MS = 5000;
const DEFAULT_FADE_DURATION_MS = 800;

function resolveServerSafeImage(media: MediaRef): { src: string; alt: string } {
    return {
        src: mediaRefUrl(media),
        alt: mediaRefAlt(media) || "",
    };
}

export default function SlideshowIntroV2({
    title,
    subtitle,
    images,
    startChildren,
    heightPreset = "full",
    slideDurationMs = DEFAULT_SLIDE_DURATION_MS,
    fadeDurationMs = DEFAULT_FADE_DURATION_MS,
    content,
    linkContentToAnchor = true,
}: SlideshowIntroV2Props) {
    const normalizedImages = (images?.length ? images : [DEFAULT_IMAGE]).map(resolveServerSafeImage);

    const slideCount = normalizedImages.length;
    const safeSlideDurationMs = Math.max(1500, slideDurationMs);
    const safeFadeDurationMs = Math.min(Math.max(250, fadeDurationMs), Math.floor(safeSlideDurationMs / 2));
    const cycleDurationMs = safeSlideDurationMs * Math.max(slideCount, 1);
    const slotPercent = 100 / Math.max(slideCount, 1);
    const fadePercent = (safeFadeDurationMs / cycleDurationMs) * 100;
    const holdPercent = Math.max(fadePercent, slotPercent - fadePercent);

    const animationName = `slideshowIntroV2Fade_${slideCount}_${safeSlideDurationMs}_${safeFadeDurationMs}`;
    const keyframes = slideCount > 1
        ? `@keyframes ${animationName} {
            0% { opacity: 0; transform: scale(1.03); }
            ${fadePercent.toFixed(4)}% { opacity: 1; transform: scale(1); }
            ${holdPercent.toFixed(4)}% { opacity: 1; transform: scale(1); }
            ${slotPercent.toFixed(4)}% { opacity: 0; transform: scale(1.01); }
            100% { opacity: 0; transform: scale(1.01); }
        }`
        : null;

    return (
        <span className="relative">
            {keyframes && <style>{keyframes}</style>}

            <section
                className={clsx(
                    "relative w-screen bottom-fade",
                    heightPreset === "full" ? "h-full-header-aware" : "h-reduced-header-aware",
                )}
                aria-label="Einleitung"
            >
                <div className={styles.sliderStage} aria-hidden={slideCount > 1}>
                    {normalizedImages.map((image, index) => (
                        <div
                            key={`${image.src}-${index}`}
                            className={clsx(styles.slide, slideCount > 1 && styles.slideAnimated)}
                            style={slideCount > 1 ? {
                                animationName,
                                animationDuration: `${cycleDurationMs}ms`,
                                animationDelay: `${-(index * safeSlideDurationMs + safeFadeDurationMs)}ms`,
                            } : undefined}
                        >
                            <Image
                                src={image.src}
                                alt={index === 0 ? image.alt : ""}
                                fill
                                sizes="100vw"
                                priority={index === 0}
                                fetchPriority={index === 0 ? "high" : "auto"}
                                className={styles.slideImage}
                            />
                        </div>
                    ))}
                </div>

                {linkContentToAnchor ? (
                    <a
                        href={`#${INTRO_ANCHOR_ID}`}
                        className={styles.contentLink}
                    >
                        <div className={clsx(styles.contentCard, "signature-border-radius glass-effect")}>
                            <div className={styles.contentBody}>
                                {startChildren}
                                {content ?? (
                                    <>
                                        <Typography variant="h1" className={styles.title}>{title}</Typography>
                                        {subtitle && <Typography component="h2" variant="subtitle2" className={styles.subtitle}>{subtitle}</Typography>}
                                    </>
                                )}
                            </div>
                        </div>
                    </a>
                ) : (
                    <div className={clsx(styles.contentLink, styles.contentStatic)}>
                        <div className={clsx(styles.contentCard, "signature-border-radius glass-effect")}>
                            <div className={styles.contentBody}>
                                {startChildren}
                                {content ?? (
                                    <>
                                        <Typography variant="h1" className={styles.title}>{title}</Typography>
                                        {subtitle && <Typography component="h2" variant="subtitle2" className={styles.subtitle}>{subtitle}</Typography>}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </section>

            <ScrollDownArrows />
        </span>
    );
}
